import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LifeBuoy, Send, CheckCircle2, Clock, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";

export default function Support() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [tickets, setTickets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/support/tickets/my");
      setTickets(data.tickets || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error("Subject and message are both required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/support/tickets", { subject: subject.trim(), message: message.trim() });
      toast.success("Ticket submitted — our team will reply shortly");
      setSubject(""); setMessage("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <AppLayout>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-nb-cyan">Help · Support</div>
        <h1 className="font-heading font-bold text-3xl text-white mt-1">
          <LifeBuoy size={24} className="inline mr-2 -mt-1"/>Support Center
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          Have a question or issue? Submit a ticket — our team will reply by email and the response will also show up on this page.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        {/* Submit form */}
        <div className="nb-card p-5 lg:col-span-2" data-testid="support-form">
          <div className="text-[10px] uppercase tracking-widest text-nb-cyan">New Ticket</div>
          <h2 className="font-heading text-white text-lg mt-1">
            <MessageSquare size={16} className="inline mr-1 -mt-1"/>Create a Support Ticket
          </h2>

          <form onSubmit={submit} className="mt-4 space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Subject</label>
              <input
                type="text"
                data-testid="support-subject"
                value={subject}
                onChange={e=>setSubject(e.target.value)}
                className="nb-input mt-1 text-sm"
                placeholder="e.g. Withdrawal not received"
                maxLength={200}
              />
              <div className="text-[10px] text-slate-500 mt-0.5 text-right">{subject.length}/200</div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Message</label>
              <textarea
                data-testid="support-message"
                value={message}
                onChange={e=>setMessage(e.target.value)}
                className="nb-input mt-1 h-40 text-sm"
                placeholder="Describe your issue in detail — Transaction ID, screenshot details, when it happened, etc."
                maxLength={5000}
              />
              <div className="text-[10px] text-slate-500 mt-0.5 text-right">{message.length}/5000</div>
            </div>

            <button
              type="submit"
              data-testid="support-submit"
              disabled={busy || !subject.trim() || !message.trim()}
              className="w-full px-5 py-2.5 rounded-xl bg-nb-cyan text-black font-heading font-bold hover:opacity-90 disabled:opacity-40"
            >
              <Send size={14} className="inline mr-1 -mt-0.5"/>
              {busy ? "Submitting…" : "Submit Ticket"}
            </button>
          </form>

          <div className="mt-5 p-3 rounded-lg border border-white/5 bg-[#0A1120]">
            <div className="text-[10px] uppercase tracking-widest text-amber-400">Reply Time</div>
            <div className="text-xs text-slate-300 mt-1">
              You will usually receive a reply within <b className="text-white">24–48 hours</b>. For urgent issues, add "URGENT" at the start of your subject.
            </div>
          </div>
        </div>

        {/* My tickets */}
      <div className="nb-card p-5 lg:col-span-3" data-testid="support-list">
        <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-nb-cyan">My Tickets</div>
              <h2 className="font-heading text-white text-lg mt-1">Your Support History</h2>
            </div>
            <span className="text-[11px] text-slate-500 font-mono">{tickets.length} total</span>
          </div>

          {loading && <div className="text-slate-500 text-sm text-center py-8">Loading…</div>}
          {!loading && tickets.length === 0 && (
            <div className="text-center py-12">
              <MessageSquare size={32} className="inline text-slate-600"/>
              <div className="text-slate-500 text-sm mt-2">You haven't created any tickets yet.</div>
              <div className="text-slate-600 text-xs mt-1">Use the form on the left to create a new ticket.</div>
            </div>
          )}

          <div className="space-y-2 max-h-[65vh] overflow-y-auto overflow-x-hidden">
            {tickets.map(t => {
              const open = openId === t.id;
              const resolved = t.status === "resolved";
              return (
                <div
                  key={t.id}
                  data-testid={`support-ticket-${t.id}`}
                  className={`rounded-lg border overflow-hidden ${resolved ? "border-[#8AE234]/20 bg-[#8AE234]/5" : "border-amber-500/20 bg-amber-500/5"}`}
                >
                  <button
                    onClick={() => setOpenId(open ? null : t.id)}
                    className="w-full flex items-start justify-between gap-3 p-3 text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${resolved ? "bg-[#8AE234]/20 text-[#8AE234]" : "bg-amber-500/20 text-amber-300"}`}>
                          {resolved ? <><CheckCircle2 size={10} className="inline mr-0.5 -mt-0.5"/>Resolved</> : <><Clock size={10} className="inline mr-0.5 -mt-0.5"/>Open</>}
                        </span>
                        <span className="text-[10px] text-slate-500 font-mono">{new Date(t.created_at).toLocaleString()}</span>
                        {t.admin_reply && !open && <span className="text-[10px] text-nb-cyan">↩ Reply</span>}
                      </div>
                      <div className="font-heading text-white text-sm mt-1 truncate">{t.subject}</div>
                    </div>
                    {open ? <ChevronUp size={16} className="text-slate-400 shrink-0"/> : <ChevronDown size={16} className="text-slate-400 shrink-0"/>}
                  </button>

                  {open && (
                    <div className="px-3 pb-3 space-y-2 min-w-0">
                      <div className="rounded border border-white/5 bg-[#0A1120] p-3 min-w-0 overflow-hidden">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Your Message</div>
                        <div className="text-slate-200 text-sm mt-1 whitespace-pre-wrap break-all" style={{overflowWrap:"anywhere", wordBreak:"break-word"}}>{t.message}</div>
                      </div>

                      {t.admin_reply ? (
                        <div className="rounded border border-[#8AE234]/20 bg-[#8AE234]/10 p-3 min-w-0 overflow-hidden">
                          <div className="text-[10px] uppercase tracking-widest text-[#8AE234]">
                            Admin Reply {t.admin_replied_at && <span className="text-slate-500 font-mono normal-case tracking-normal">· {new Date(t.admin_replied_at).toLocaleString()}</span>}
                          </div>
                          <div className="text-slate-100 text-sm mt-1 whitespace-pre-wrap break-all" style={{overflowWrap:"anywhere", wordBreak:"break-word"}}>{t.admin_reply}</div>
                        </div>
                      ) : (
                        <div className="rounded border border-white/5 bg-[#0A1120] p-3 text-center">
                          <Clock size={16} className="inline text-amber-400 -mt-0.5"/>
                          <span className="text-xs text-slate-400 ml-1">Admin has not replied yet — please wait</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
